import React, { useRef, useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Form, Button, InputNumber } from 'antd';
import notification from '../../../../components/notification';
import { toggleApplicationSessionSwapClassModal } from '../../../../redux/ui/actions';
import { swapApplicationSession } from '../../../../redux/request/actions';
import CourseClassSelect from '../../CourseClass/components/course-class-select';
import RescheduleCourseSessionSelect from './reschedule-course-session-select';
import _ from 'lodash';

const FormItem = Form.Item;

function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
}

function ApplicationSessionSwapClassModal({ form, ...rest }) {
    const { getFieldDecorator } = form;
    const [courseClassId, setCourseClassId] = useState(undefined);
    const dispatch = useDispatch();

    const open = useSelector(state => state.toggleApplicationSessionSwapClassModal.open);
    const session = useSelector(state => state.toggleApplicationSessionSwapClassModal.session);
    const courseSessions = useSelector(state => state.getCourseSessionsForReschedule.data);
    const info = useSelector(state => state.toggleApplicationSessionSwapClassModal.info);

    useEffect(() => {
        if (session) {
            setCourseClassId(session.course_class_id);
        }
    }, [session, setCourseClassId]);

    useEffect(() => {
        form.setFieldsValue({
            course_session_id: undefined,
            initial_session_number: null
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [courseClassId]);

    const handleApiResponse = useCallback((prevLoading, response) => {
        if (prevLoading === true && !response.isFetching && response.success) {
            dispatch(toggleApplicationSessionSwapClassModal({ open: false, session: undefined }))
            form.resetFields();
            setCourseClassId(undefined);
            notification('success', 'Successfully', 'Successfully Reserve');

        }
        if (prevLoading === true && !response.isFetching && response.error) {
            console.log(response.error);
            notification('error', 'Error', response.errorMessage);
        }
    }, [dispatch, form]);

    const response = useSelector(state => state.swapApplicationSession);
    const saving = response.isFetching;

    const prevReserveLoading = usePrevious(response.isFetching);
    useEffect(() => {
        handleApiResponse(prevReserveLoading, response);
    }, [prevReserveLoading, response, handleApiResponse]);

    function handleClose() {
        dispatch(toggleApplicationSessionSwapClassModal({ open: false, session: undefined }))
        form.resetFields();
        setCourseClassId(undefined);
    }

    function handleSubmit() {
        form.validateFieldsAndScroll((err, values) => {
            if (!err) {
                const selectedClassId = courseClassId ?? values.course_class_id;
                const courseSession = _.find(courseSessions || [], (f) => f.id === values.course_session_id);

                if (!courseSession) {
                    notification('error', 'Error', 'Session data may have changed. Please close and reopen the dialog, then try again.');
                    return;
                }

                if (courseSession.class_id !== selectedClassId) {
                    notification('error', 'Error', 'Session list is out of sync with selected class. Please close and reopen the dialog, then try again.');
                    return;
                }

                let data = {
                    course_class_id: selectedClassId,
                    initial_session_number: values.initial_session_number
                }
                if (courseSession.dummy) {
                    data.start_date = courseSession.start_date;
                } else {
                    data.course_session_id = courseSession.id;
                }
                dispatch(swapApplicationSession({ id: session.application_session_id, data }));
            }
        });
    }

    let student_info = info && session ? `${info.student_name} in ${info.class_name} (x${session.display_number}) on ${info.session_date.format('YYYY-MM-DD')}` : ''

    return (
        <Modal
            visible={open}
            title={
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span>Swap Session</span>
                    <span style={{ fontSize: '0.75em', color: '#777' }}>{student_info}</span>
                </div>
            }
            onCancel={handleClose}
            footer={[
                <Button
                    key="back"
                    onClick={handleClose}
                    loading={saving}
                >
                    Cancel
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    htmlType="submit"
                    onClick={handleSubmit}
                    loading={saving}
                >
                    Swap
                </Button>
            ]}
        >
            {session && <Form>
                <FormItem
                    label="Class"
                >
                    {getFieldDecorator('course_class_id', {
                        rules: [
                            {
                                required: true
                            }
                        ],
                        initialValue: session.course_class_id
                    })(
                        <CourseClassSelect onChange={(value) => setCourseClassId(value)} type='GENERAL' />
                    )}
                </FormItem>
                {
                    courseClassId !== undefined &&
                    <FormItem
                        label="Session"
                    >
                        {getFieldDecorator('course_session_id', {
                            rules: [
                                {
                                    required: true
                                }
                            ],
                        })(
                            <RescheduleCourseSessionSelect course_class_id={courseClassId} />
                        )}
                    </FormItem>
                }
                {
                    courseClassId !== undefined &&
                    <FormItem
                        label="Initial Session Number"
                    >
                        {getFieldDecorator('initial_session_number', {

                        })(
                            <InputNumber />
                        )}
                    </FormItem>
                }
            </Form>}
        </Modal>
    )
}

export default Form.create()(ApplicationSessionSwapClassModal);
